import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ErrorIcon } from 'assets/icons/toast-error.svg';
import { COLUMN_LABELS } from 'constants/general';
import errorMessagesFormatted from 'utils/formattedErrorText';
import formatCamelCase, { formatTextTitle, parseValuesString } from 'utils/formatText';

import type ISourceType from '../interfaces/ISourceType';
import type IErrorListContent from './interfaces/IErrorListContent';

import styles from '../sass/QuestionErrorModal.module.scss';

function ErrorListContent({
  title,
  questionErrorList,
  activePopover,
  handleShowMoreClick,
  handleShowMoreKeyDown,
}: IErrorListContent) {
  const { t } = useTranslation();

  const getRowErrorSources = (rowData: Record<string, ISourceType[]>): ISourceType[] => (
    Object.values(rowData ?? {}).filter(Array.isArray).flat() as ISourceType[]
  );

  const getColumnLabel = (column: string): string => {
    if (column === COLUMN_LABELS.TEXT) return COLUMN_LABELS.QUESTION;
    if (column === COLUMN_LABELS.TYPE) return COLUMN_LABELS.QUESTION_TYPE;
    return formatCamelCase(column);
  };

  return (
    <div>
      <p>{`${title}. ${t('common.validationErrors')}`}</p>
      <div className={styles.modalBodyList}>
        <ul className={styles.errorList}>
          {Object.entries(questionErrorList || {}).map(([rowKey, rowData]) => (
            <li key={rowKey} className={styles.errorRowBlock}>
              <div className={styles.rowHeader}>
                <b>{`${t('dataSetsManagement.importErrorsModal.errorRow')}`}</b>
                {' '}
                {rowKey}
              </div>
              {getRowErrorSources(rowData as Record<string, ISourceType[]>).map((source) => {
                const parsedValues = parseValuesString(source.params?.values);
                const formattedValues = parsedValues.map((v) => formatTextTitle(v, false));
                const popoverId = `${rowKey}-${source.column}-${source.error}-${source.errorCode}`;
                return (
                  <div
                    key={popoverId}
                    className={styles.columnErrorItem}
                  >
                    <span className={styles.columnLabel}>
                      <b>{t('dataSetsManagement.importErrorsModal.errorColumn')}</b>
                      {' '}
                      {getColumnLabel(source.column)}
                    </span>
                    <div className={styles.errorContent}>
                      <span className={styles.errorMessageBlock}>
                        <ErrorIcon
                          className={styles.errorIcon}
                          aria-hidden
                        />
                        <span className={styles.errorMessage}>
                          {errorMessagesFormatted(source.error, source.params)}
                          {parsedValues.length > 0 && (
                          <>
                            {' '}
                            <span
                              className={styles.showMoreTrigger}
                              role="button"
                              tabIndex={0}
                              aria-label={t('common.showMoreValues')}
                              aria-expanded={activePopover?.id === popoverId}
                              onKeyDown={handleShowMoreKeyDown}
                              onClick={(e) => handleShowMoreClick(e, popoverId, formattedValues)}
                            >
                              {t('common.showMore')}
                            </span>
                          </>
                          )}
                        </span>
                      </span>
                    </div>
                  </div>
                );
              })}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default ErrorListContent;
